import { validateUrl } from '@/utils/urlValidation';
import { Webhook } from '@dynamic-flow/types';
import React from 'react';
import { useForm } from 'react-hook-form';

interface WebhookFormProps {
  onSubmit: (values: Webhook) => void;
  editMode?: boolean;
  defaultValues?: Partial<Webhook>;
}

const WebhookForm: React.FC<WebhookFormProps> = (props) => {
  const { onSubmit, defaultValues = {}, editMode = false } = props;

  const {
    handleSubmit,
    register,
    formState: { errors },
  } = useForm<Webhook>({ defaultValues: { isActive: true, ...defaultValues } });

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-2">
      <input
        className={`w-full px-3 py-2 border rounded text-sm ${
          errors.name
            ? 'border-red-500 focus:ring-red-500'
            : 'border-gray-300 focus:ring-blue-500'
        } focus:outline-none focus:ring-2`}
        placeholder="e.g., Payment Notifications, User Signup Events, Order Updates"
        {...register('name', {
          required: 'Name is required',
        })}
      />
      {errors.name && (
        <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
      )}
      <input
        className={`w-full px-3 py-2 border rounded text-sm ${
          errors.url
            ? 'border-red-500 focus:ring-red-500'
            : 'border-gray-300 focus:ring-blue-500'
        } focus:outline-none focus:ring-2`}
        placeholder="https://your-app.com/api/webhooks/notifications"
        {...register('url', {
          validate: (value) => {
            const { isValid, errorMessage } = validateUrl(value, true);
            return isValid ? undefined : errorMessage;
          },
        })}
      />
      {errors.url && (
        <p className="mt-1 text-sm text-red-600">{errors.url.message}</p>
      )}
      <input
        placeholder="whsec_1a2b3c4d5e6f7g8h9i0j..."
        className="w-full px-3 py-2 border rounded text-sm"
        {...register('secret')}
      />

      <label className="inline-flex items-center cursor-pointer">
        <input
          type="checkbox"
          className="sr-only peer"
          {...register('isActive', { required: 'This field is required' })}
        />
        <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600 dark:peer-checked:bg-blue-600"></div>
        <span className="ms-3 text-sm font-medium text-gray-900 dark:text-gray-300">
          Enabled
        </span>
      </label>

      <button
        className="px-3 py-1 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700"
        type="submit"
      >
        {editMode ? 'Update' : 'Add'}
      </button>
    </form>
  );
};

export default WebhookForm;
